<script setup>
import { onMounted } from 'vue'
import { openSupport } from '@/composables/useSupport'
import { Check } from 'lucide-vue-next'
import Card from '@/Components/ui/card/Card.vue'
import CardContent from '@/Components/ui/card/CardContent.vue'
import Button from '@/Components/ui/button/Button.vue'
import Alert from '@/Components/ui/alert/Alert.vue'
import AlertDescription from '@/Components/ui/alert/AlertDescription.vue'

const props = defineProps({
  application: {
    type: Object,
    required: true
  }
})

// Clear local storage when component mounts
onMounted(() => {
  localStorage.removeItem('tutorApplicationSlug')
  localStorage.removeItem('tutorApplicationStep')
  localStorage.removeItem('tutor_onboarding_slug')
  localStorage.removeItem('tutor_onboarding_step')
})

const goToHome = () => {
  window.location.href = '/'
}

const goToTutors = () => {
  window.location.href = '/tutors'
}
</script>

<template>
  <section class="py-8 md:py-16">
    <div class="max-w-4xl mx-auto px-4 md:px-8">
      <!-- Success Card -->
      <Card class="overflow-hidden shadow-xl border-gray-100">
        <!-- Success Icon Header -->
        <div class="bg-gradient-to-r from-brand-blue to-blue-600 px-4 md:px-8 py-12 text-center">
          <div class="inline-flex items-center justify-center w-24 h-24 bg-white rounded-full shadow-lg mb-6 animate-bounce">
            <span class="material-symbols-outlined text-green-500 text-6xl">check_circle</span>
          </div>
          <h1 class="text-2xl md:text-3xl md:text-4xl font-bold text-white mb-3">
            Application Submitted Successfully!
          </h1>
          <p class="text-blue-100 text-lg">
            Thank you for applying to become a tutor at Bindle
          </p>
        </div>

        <!-- Content -->
        <CardContent class="px-4 md:px-8 py-10">
          <!-- What Happens Next Section -->
          <div class="mb-8">
            <h2 class="text-2xl font-bold text-gray-900 mb-4 flex items-center">
              <span class="material-symbols-outlined text-brand-blue mr-3">schedule</span>
              What Happens Next?
            </h2>
            <div class="space-y-4">
              <div class="flex items-start space-x-4 p-4 bg-blue-50 rounded-xl border border-blue-100">
                <div class="flex-shrink-0 w-8 h-8 bg-brand-blue text-white rounded-full flex items-center justify-center font-bold">
                  1
                </div>
                <div>
                  <h3 class="font-semibold text-gray-900 mb-1">Email Confirmation</h3>
                  <p class="text-gray-600 text-sm">
                    We've sent a confirmation email to <span class="font-medium text-brand-blue">{{ application.email }}</span>. Please check your inbox (and spam folder).
                  </p>
                </div>
              </div>

              <div class="flex items-start space-x-4 p-4 bg-green-50 rounded-xl border border-green-100">
                <div class="flex-shrink-0 w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center font-bold">
                  2
                </div>
                <div>
                  <h3 class="font-semibold text-gray-900 mb-1">Application Review</h3>
                  <p class="text-gray-600 text-sm">
                    Our team will carefully review your application, qualifications, and documents. This typically takes 3-5 business days.
                  </p>
                </div>
              </div>

              <div class="flex items-start space-x-4 p-4 bg-purple-50 rounded-xl border border-purple-100">
                <div class="flex-shrink-0 w-8 h-8 bg-purple-500 text-white rounded-full flex items-center justify-center font-bold">
                  3
                </div>
                <div>
                  <h3 class="font-semibold text-gray-900 mb-1">Decision Notification</h3>
                  <p class="text-gray-600 text-sm">
                    You'll receive an email with our decision. If approved, we'll guide you through the next steps to start tutoring.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <!-- Important Information -->
          <Alert class="bg-amber-50 border-amber-200 mb-8">
            <span class="material-symbols-outlined text-amber-600">info</span>
            <AlertDescription>
              <h3 class="font-semibold text-amber-900 mb-2">Important Information</h3>
              <ul class="space-y-2 text-sm text-amber-800">
                <li class="flex items-start">
                  <Check class="mr-2 mt-0.5 size-4 shrink-0 text-warning" />
                  <span>Please ensure your email is accessible as we'll send all updates there</span>
                </li>
                <li class="flex items-start">
                  <Check class="mr-2 mt-0.5 size-4 shrink-0 text-warning" />
                  <span>Your documents must be valid and up-to-date. Invalid documents may delay processing</span>
                </li>
                <li class="flex items-start">
                  <Check class="mr-2 mt-0.5 size-4 shrink-0 text-warning" />
                  <span>You can check your application status by contacting our support team</span>
                </li>
              </ul>
            </AlertDescription>
          </Alert>

          <!-- Application Reference -->
          <div class="bg-gray-50 rounded-xl p-6 mb-8">
            <h3 class="font-semibold text-gray-900 mb-3 flex items-center">
              <span class="material-symbols-outlined text-gray-600 mr-2">confirmation_number</span>
              Your Application Reference
            </h3>
            <div class="bg-white border-2 border-dashed border-gray-300 rounded-lg p-4 font-mono text-center">
              <span class="text-gray-500 text-sm">Reference ID</span>
              <p class="text-brand-blue font-bold text-lg mt-1">{{ application.slug }}</p>
              <p class="text-xs text-gray-500 mt-2">Keep this reference for your records</p>
            </div>
          </div>

          <!-- Action Buttons -->
          <div class="flex flex-col sm:flex-row gap-4">
            <Button
              @click="goToHome"
              class="flex-1 py-6 shadow-lg shadow-blue-200"
              size="lg"
            >
              <span class="material-symbols-outlined mr-2">home</span>
              Return to Home
            </Button>
            <Button
              @click="goToTutors"
              variant="outline"
              class="flex-1 py-6 border-2 border-brand-blue text-brand-blue hover:bg-blue-50"
              size="lg"
            >
              <span class="material-symbols-outlined mr-2">school</span>
              Browse Tutors
            </Button>
          </div>
        </CardContent>

        <!-- Footer Note -->
        <div class="bg-gray-50 px-8 py-6 border-t border-gray-100">
          <p class="text-center text-sm text-gray-600">
            Questions?
            <button type="button" class="font-medium text-brand hover:underline" @click="openSupport()">
              Send us a message
            </button>
          </p>
        </div>
      </Card>

      <!-- Additional Help Card -->
      <Card class="mt-6 shadow-sm">
        <CardContent class="p-6">
          <div class="flex items-center justify-between">
            <div class="flex items-center space-x-4">
              <div class="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <span class="material-symbols-outlined text-brand-blue text-2xl">support_agent</span>
              </div>
              <div>
                <h3 class="font-semibold text-gray-900">Need Help?</h3>
                <p class="text-sm text-gray-600">Our support team is here to assist you</p>
              </div>
            </div>
            <Button variant="outline" class="bg-gray-100" @click="openSupport()">
              Contact support
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  </section>
</template>

<style scoped>
@keyframes bounce {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
}

.animate-bounce {
  animation: bounce 2s ease-in-out infinite;
}
</style>
